import { useState, useEffect, useCallback } from 'react';
import sha1 from 'sha1';
import { getLiteralsLogin } from './services';

const DEFAULT_LANG = 'en';
const FM_LANGS = ['fr', 'es', 'en', 'de', 'pt', 'it'];

export function getLang() {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (params?.lang) return params.lang;

    let userLang = navigator.language || navigator.userLanguage;
    userLang = userLang.substring(0, 2);
    if (FM_LANGS.includes(userLang)) return userLang;
    else return DEFAULT_LANG;
}

export function useLoginLiterals(overLang) {
    const [loading, setLoading] = useState(true);
    const [literals, setLiterals] = useState();
    let lang = overLang;
    if (!lang) lang = getLang();

    const getLiteral = useCallback(
        (key, params) => {
            let literal = literals?.[key] || key;
            if (params && params.length > 0 && literal) {
                literal = literal.replace(/\%([0-9]+)\$s/g, '%@');
                if (literal.includes('%@')) {
                    const appearances = literal.match(new RegExp('%@', 'g')).length;
                    for (let i = 0; i < appearances && i < params.length; i++) {
                        literal = literal.replace('%@', params[i]);
                    }
                }
            }
            return literal;
        },
        [literals],
    );

    useEffect(() => {
        if (literals) return;

        getLiteralsLogin(lang)
            .then(({ data }) => {
                setLiterals(data || {});
                setLoading(false);
            })
            .catch((e) => {
                setLoading(false);
                console.error(e);
            });
    }, [lang, literals]);

    return { loading, getLiteral, lang };
}

export function parsePolicies(data) {
    let policy = [];

    let {
        requireLowerCase,
        requireLowerCaseLetters,
        lowercaseLettersMin,
        lowerCaseLettersMinNumber,
        regexLowerCase,
        requireUpperCase,
        requireUpperCaseLetters,
        uppercaseLettersMin,
        upperCaseLettersMinNumber,
        regexUpperCase,
        requireDigits,
        digitsMin,
        digitsMinNumber,
        regexDigits,
        minLength,
        maxLength,
        requireSpecialLetters,
        specialLettersMin,
        specialLettersMinNumber,
        regexSpecial,
    } = data;

    requireLowerCase = requireLowerCase || requireLowerCaseLetters;
    lowercaseLettersMin = lowercaseLettersMin || lowerCaseLettersMinNumber;
    requireUpperCase = requireUpperCase || requireUpperCaseLetters;
    uppercaseLettersMin = uppercaseLettersMin || upperCaseLettersMinNumber;
    digitsMin = digitsMin || digitsMinNumber;
    specialLettersMin = specialLettersMin || specialLettersMinNumber;

    if (requireLowerCase && lowercaseLettersMin) {
        let literal =
            lowercaseLettersMin > 1
                ? 'label_minimum_lowercase_characters_password_policy'
                : 'label_minimum_lowercase_characters_password_policy_one';

        policy.push({
            literal,
            number: lowercaseLettersMin,
            condition(password) {
                let reg = new RegExp(regexLowerCase);
                return password && reg.test(password);
            },
        });
    }

    if (requireUpperCase && uppercaseLettersMin) {
        let literal =
            uppercaseLettersMin > 1
                ? 'label_minimum_uppercase_characters_password_policy'
                : 'label_minimum_uppercase_characters_password_policy_one';

        policy.push({
            literal,
            number: uppercaseLettersMin,
            condition(password) {
                let reg = new RegExp(regexUpperCase);
                return password && reg.test(password);
            },
        });
    }

    if (requireDigits && digitsMin) {
        let literal =
            digitsMin > 1
                ? 'label_minimum_numbers_password_policy'
                : 'label_minimum_numbers_password_policy_one';

        policy.push({
            literal,
            number: digitsMin,
            condition(password) {
                let reg = new RegExp(regexDigits);
                return password && reg.test(password);
            },
        });
    }

    if (minLength > 1) {
        let literal = 'label_minimum_characters_password_policy';
        policy.push({
            literal,
            number: minLength,
            condition(password) {
                return password && password.length >= minLength;
            },
        });
    }

    if (maxLength > 1) {
        let literal = 'label_maximum_characters_password_policy';
        policy.push({
            literal,
            number: maxLength,
            condition(password) {
                return password && password.length <= maxLength;
            },
        });
    }

    if (requireSpecialLetters && specialLettersMin) {
        let literal =
            specialLettersMin > 1
                ? 'label_minimum_special_characters_password_policy'
                : 'label_minimum_special_characters_password_policy_one';

        let regex = regexSpecial;
        policy.push({
            literal,
            number: specialLettersMin,
            condition(password) {
                let reg = new RegExp(regex);
                return password && reg.test(password);
            },
        });
    }

    return policy;
}

export function sha1FM(toHash) {
    let sha1Code = sha1(toHash);
    return sha1Code
        .match(/(..?)/g)
        .reduce((result, pair) => {
            pair[0] === '0' ? result.push(pair[1]) : result.push(pair);
            return result;
        }, [])
        .join('');
}
